import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntitySelect, invalidateEntity } from "@/components/EntitySelect";
import { Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * Config-driven form dialog. fields: [{name,label,type,options,endpoint,params,labelKey,required,full}]
 * On HTTP 409 with `confirmKey`, asks the user and resubmits with {[confirmKey]: true}.
 */
export function FormDialog({ title, triggerLabel, triggerTestId, triggerVariant, triggerSize, icon = true,
  fields, initial = {}, method = "post", url, transform, confirmKey, successMsg = "Kaydedildi", onDone, disabled }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const send = async (extra = {}) => {
    const body = { ...(transform ? transform(values) : values), ...extra };
    Object.keys(body).forEach((k) => (body[k] === "" || body[k] === undefined) && delete body[k]);
    return api[method](url, body);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      try {
        await send();
      } catch (err) {
        if (err.response?.status === 409 && confirmKey && window.confirm(`${errMsg(err)}\n\nDevam edilsin mi?`)) {
          await send({ [confirmKey]: true });
        } else throw err;
      }
      toast.success(successMsg);
      setOpen(false);
      setValues(initial);
      onDone && onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setValues(initial); }}>
      <DialogTrigger asChild>
        <Button variant={triggerVariant} size={triggerSize} disabled={disabled} data-testid={triggerTestId}>
          {icon && <Plus className="w-4 h-4 mr-1.5" />}{triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.name} className={`space-y-1.5 ${f.full || f.type === "textarea" ? "sm:col-span-2" : ""}`}>
              {f.type !== "checkbox" && <Label>{f.label}{f.required && " *"}</Label>}
              <Field f={f} value={values[f.name]} onChange={(v) => set(f.name, v)} />
            </div>
          ))}
          <DialogFooter className="sm:col-span-2">
            <Button type="submit" disabled={busy} data-testid={`${triggerTestId}-save`}>{busy ? "Kaydediliyor..." : "Kaydet"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ f, value, onChange }) {
  const tid = `field-${f.name}`;
  const [rev, setRev] = useState(0);
  const quickAdd = async () => {
    const name = window.prompt(f.quickAdd);
    if (!name?.trim()) return;
    try {
      const r = await api.post(f.endpoint, { name: name.trim() });
      invalidateEntity(f.endpoint);
      setRev((x) => x + 1);
      onChange(r.data.id);
      toast.success("Yeni kayıt eklendi");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (f.type === "entity")
    return (
      <div className="flex gap-2">
        <div className="flex-1 min-w-0">
          <EntitySelect key={rev} endpoint={f.endpoint} params={f.params} labelKey={f.labelKey} value={value} onChange={onChange}
            allowEmpty={!f.required} testId={tid} />
        </div>
        {f.quickAdd && <Button type="button" variant="outline" size="sm" className="h-10" onClick={quickAdd} data-testid={`${tid}-quick-add`}>+ Yeni</Button>}
      </div>
    );
  if (f.type === "select")
    return (
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger data-testid={tid}><SelectValue placeholder="Seçiniz" /></SelectTrigger>
        <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  if (f.type === "checkbox")
    return (
      <label className="flex items-center gap-2 text-sm pt-6 cursor-pointer">
        <Checkbox checked={!!value} onCheckedChange={(c) => onChange(!!c)} data-testid={tid} />{f.label}
      </label>
    );
  if (f.type === "textarea")
    return <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  return (
    <Input type={f.type || "text"} required={f.required} min={f.type === "number" ? 0 : undefined} step={f.step || "any"}
      value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />
  );
}
